import numbro from "numbro";

export const cryptoMantissa = 5;
export const fiatMantissa = 2;

/** Is this asset code/symbol a fiat currency? (mirrors FormatUtils.isFiatCurrency on iOS) */
import { fiatCurrencyCodes } from "../config/fiatCurrencies";
const FIAT_SYMBOLS = new Set(["$", "£", "€", "¥", "₩", "S$", "A$", "C$", "HK$", "NZ$", "Fr", "kr"]);
export const isFiatCurrency = (asset?: string | null): boolean => {
  if (!asset) return false;
  const a = asset.trim();
  return fiatCurrencyCodes.includes(a.toUpperCase()) || FIAT_SYMBOLS.has(a);
};
import { dayjs } from "@moonscape/shared";

export const parseDateTime = (datetime: string) => {
  if (!datetime) return null;

  try {
    // Handle format: 2021-03-02 16:43:20.07+00
    // Also handles: 2024-05-18T13:29:47+00:00 (ISO 8601 with timezone)
    if (datetime.includes("+00")) {
      // Replace +00:00 or +00 with Z, and ensure T separator
      const normalized = datetime
        .replace(" ", "T")
        .replace(/\+00:00$/, "Z")
        .replace(/\+00$/, "Z");
      return dayjs.utc(normalized);
    }
    // Handle ISO format: 2021-04-04T14:15:46.208Z or simple format: 2025-04-05 09:04:24
    return dayjs.utc(datetime);
  } catch (error) {
    console.error("Error parsing datetime:", error);
    return null;
  }
};

export const formattedAmount = (txn, ccy, isFiatCurrency = false) => {
  const { side, buyqty, sellqty } = txn;

  // Determine effectiveAmount based on side and availability of buyqty and sellqty
  let effectiveAmount;
  let counterAmount;
  if (side === "buy") {
    effectiveAmount = buyqty ?? sellqty; // Use buyqty if available, otherwise fallback to sellqty
    counterAmount = sellqty ?? buyqty; // Inverse of effectiveAmount
  } else if (side === "sell") {
    effectiveAmount = sellqty ?? buyqty; // Use sellqty if available, otherwise fallback to buyqty
    counterAmount = buyqty ?? sellqty; // Inverse of effectiveAmount
  } else {
    // Handle other cases or throw an error
    throw new Error("Invalid transaction side");
  }

  console.log(
    "Effective Amount:",
    effectiveAmount,
    "Counter Amount:",
    counterAmount
  );

  const parsedAmount = parseFloat(effectiveAmount);
  if (Number.isNaN(parsedAmount)) {
    return effectiveAmount; // Return the original input if it can't be parsed to a number
  }

  let numberFormat;
  if (isFiatCurrency) {
    numberFormat = numbro(parsedAmount).format({
      thousandSeparated: true,
      mantissa: fiatMantissa, // For fiat currencies, always use 2 decimal places
    });
  } else {
    numberFormat = numbro(parsedAmount).format({
      thousandSeparated: true,
      mantissa: parsedAmount < 1 ? 8 : 2, // Use 8 decimal places for small amounts, otherwise 2
    });
  }

  return numberFormat;
};

/** Fiat prices: always 2dp. Sub-cent values keep precision so they don't collapse to 0.00. */
export const formatPrice = (price) => {
  const numberFormat =
    Math.abs(price) < 0.01 && price !== 0
      ? numbro(price).format({
          trimMantissa: false,
          mantissa: cryptoMantissa,
        })
      : numbro(price).format({
          thousandSeparated: true,
          trimMantissa: false,
          mantissa: fiatMantissa,
        });

  return numberFormat;
};

/** format amounts 0.00 and trunc 0.10000000 to 0.1 BTC e.g. */
export const formatAmount = (
  amount,
  // Typed, not just defaulted: a bare `= undefined` infers the parameter AS
  // `undefined`, so every caller passing a real currency symbol is a type error.
  ccy: string | undefined = undefined,
  // undefined → derived from ccy, so fiat amounts get 2dp even when callers don't pass the flag
  isFiat: boolean | undefined = undefined,
  hideCents = false,
  noAbbreviate = false  // Skip K/M/B abbreviation, use thousand separators instead
) => {
  const fiatMode = isFiat ?? isFiatCurrency(ccy);
  // early return if amount is null or undefined
  // Check if numberFormat is null or undefined and return "-" in that case
  if (amount == null) {
    return "-";
  }
  // Remove any "+" or "-" prefixes
  const sanitizedAmount = String(amount).replace(/^[-+]/, "");

  const parsedAmount = parseFloat(sanitizedAmount);
  if (Number.isNaN(parsedAmount)) {
    return sanitizedAmount;
  }

  const absAmount = Math.abs(amount);

  // Handle very small numbers (e.g. 6.1439e-05) by showing up to 8 decimal places
  if (absAmount < 0.01 && absAmount > 0) {
    // Cap at 8 decimal places maximum
    const maxDecimals = 8;
    const decimalPlaces = parsedAmount.toString().includes("e")
      ? Math.min(Math.abs(Math.floor(Math.log10(absAmount))) + 4, maxDecimals)
      : maxDecimals;

    // Format with specified decimals and remove trailing zeros. Trim on the
    // fixed-decimal string, not via Number.toString() - that reintroduces
    // exponential notation (e.g. "1e-8") for magnitudes below 1e-6.
    const formatted = parsedAmount
      .toFixed(decimalPlaces)
      .replace(/(\.\d*?)0+$/, "$1")
      .replace(/\.$/, "");
    return ccy ? `${ccy} ${formatted}` : formatted;
  }

  let numberFormat;
  if (fiatMode) {
    // For fiat, always use consistent decimal formatting (no k/m/b abbreviations)
    numberFormat = numbro(parsedAmount).format({
      thousandSeparated: true,
      mantissa: hideCents ? 0 : fiatMantissa, // Use 0 decimals if hideCents, otherwise 2
      trimMantissa: hideCents,
    });
  } else {
    // Only use K/M/B abbreviation if noAbbreviate is false
    numberFormat = noAbbreviate ? null : formatBigNumberLargerThanNumber(parsedAmount);
    if (!numberFormat) {
      numberFormat =
        absAmount < 1
          ? numbro(parsedAmount).format({
              trimMantissa: true,
              mantissa: cryptoMantissa,
            })
          : numbro(parsedAmount).format({
              thousandSeparated: true,
              trimMantissa: true, // Trim trailing zeros for crypto
              mantissa: cryptoMantissa, // Use 5 decimals for crypto amounts >= 1
            });
    }
  }

  return ccy ? `${ccy} ${numberFormat}` : numberFormat;
};

export const formatInteger = (amount, ccy) => {
  const parsedAmount = parseFloat(amount);
  if (Number.isNaN(parsedAmount)) {
    return amount;
  }
  const numberFormat = numbro(parsedAmount).format({
    trimMantissa: true,
    mantissa: 0,
  });

  return numberFormat;
};

/** Convert currency code to symbol */
export const getCurrencySymbol = (currencyCode: string): string => {
  switch (currencyCode?.toUpperCase()) {
    case "USD":
      return "$";
    case "GBP":
      return "£";
    case "EUR":
      return "€";
    case "SGD":
      return "S$";
    case "AUD":
      return "A$";
    case "CAD":
      return "C$";
    case "CHF":
      return "Fr";
    case "JPY":
      return "¥";
    case "KRW":
      return "₩";
    case "HKD":
      return "HK$";
    case "AED":
      return "د.إ";
    case "NZD":
      return "NZ$";
    case "SEK":
      return "kr";
    default:
      return currencyCode; // Return the currency code if no symbol found
  }
};

/** Fiat money: always 2dp (e.g. "£ 1,234.50"). Sub-cent values keep precision so they don't collapse to 0.00. */
export const formatCurrency = (amount, ccy = "£") => {
  const parsedAmount = parseFloat(amount);
  if (Number.isNaN(parsedAmount)) {
    return amount;
  }

  const absAmount = Math.abs(parsedAmount);

  const numberFormat =
    absAmount < 0.01 && absAmount > 0
      ? numbro(parsedAmount).format({
          trimMantissa: true,
          mantissa: cryptoMantissa,
        })
      : numbro(parsedAmount).format({
          thousandSeparated: true,
          trimMantissa: false,
          mantissa: fiatMantissa,
        });

  return `${ccy} ${numberFormat}`;
};

export const formatCurrencySummary = (amount, ccy = "£") => {
  const parsedAmount = parseFloat(amount);
  if (Number.isNaN(parsedAmount)) {
    return amount;
  }
  const abs_amount = Math.abs(parseInt(amount));
  let numberFormat;
  if (abs_amount >= 1e12) {
    numberFormat = (abs_amount / 1e12).toFixed(2) + "T";
  } else if (abs_amount >= 1e9) {
    numberFormat = (abs_amount / 1e9).toFixed(2) + "B";
  } else if (abs_amount >= 1e6) {
    numberFormat = (abs_amount / 1e6).toFixed(2) + "M";
  } else if (abs_amount >= 1e3) {
    numberFormat = numberFormat = (abs_amount / 1e3).toFixed(2) + "K";
  } else {
    // Below the K threshold: plain fiat, always 2dp
    numberFormat = numbro(parsedAmount).format({
      thousandSeparated: true,
      trimMantissa: false,
      mantissa: fiatMantissa,
    });
  }
  return `${ccy} ${numberFormat}`;
};

export const formatNumberNanToZero = (value) => {
  return value | 0;
};

export function formatBigNumberLargerThanNumber(
  value: number,
  minNumber: number = 1_0000,
  numFix: number = 3
) {
  if (value === null || value === undefined) {
    return "";
  }

  if (Math.abs(value) < 1e-3 && value !== 0) {
    return value.toExponential(2); // ex: 1.23e-6
  }

  // Return null for values < minNumber so caller can use numbro with thousandSeparated
  if (!value || value < minNumber)
    return null;

  const format = (val: number, suffix: string) => {
    const formatted = val.toFixed(numFix).replace(/\.?0+$/, ""); // delete .000
    return formatted + suffix;
  };

  if (value >= 1_000_000_000) {
    return format(value / 1_000_000_000, "B");
  } else if (value >= 1_000_000) {
    return format(value / 1_000_000, "M");
  } else if (value >= 1_000) {
    return format(value / 1_000, "K");
  }

  return value.toString(); // keep the same < 1000
}

export function timeAgoByMax(strDate: string | null | undefined): string | null {
  // Return null if no date provided
  if (!strDate) {
    return null;
  }

  // Parse the input date
  const inputDate = new Date(strDate);
  if (isNaN(inputDate.getTime())) {
    return null; // Handle invalid date inputs
  }

  const now = new Date();

  let years = now.getFullYear() - inputDate.getFullYear();
  let months = now.getMonth() - inputDate.getMonth();
  let days = now.getDate() - inputDate.getDate();
  let hours = now.getHours() - inputDate.getHours();
  let minutes = now.getMinutes() - inputDate.getMinutes();
  let seconds = now.getSeconds() - inputDate.getSeconds();

  if (seconds < 0) {
    seconds += 60;
    minutes--;
  }
  if (minutes < 0) {
    minutes += 60;
    hours--;
  }
  if (hours < 0) {
    hours += 24;
    days--;
  }
  if (days < 0) {
    const previousMonth = new Date(now.getFullYear(), now.getMonth(), 0); // get the end of the month
    days += previousMonth.getDate();
    months--;
  }
  if (months < 0) {
    months += 12;
    years--;
  }

  // Return the most relevant time unit
  if (years > 0) return `${years}y`;

  if (months > 0) return `${months}mo`;

  if (days > 0) return `${days}d`;

  if (hours > 0) return `${hours}h`;

  if (minutes > 0) return `${minutes}m`;

  if (seconds > 0) return `${seconds}s`;

  return `${seconds}s`;
}


export function formatStringEclipse(str: string, len: number = 6): string {
  if (str.length <= len) return str;

  const start = str.slice(0, len/2);
  const end = str.slice(-3);
  return `${start}...${end}`;
}

// --- Tax timezone formatting ---
import utcPlugin from "dayjs/plugin/utc";
import timezonePlugin from "dayjs/plugin/timezone";

dayjs.extend(utcPlugin);
dayjs.extend(timezonePlugin);

/**
 * Format a date in the user's tax timezone (IANA identifier).
 * Falls back to UTC when tz is null/undefined.
 */
export function formatInTaxTimezone(
  date: string | number | Date,
  tz: string | null | undefined,
  format: string = "MMM D YYYY, HH:mm:ss"
): string {
  const d = dayjs.utc(date);
  if (!tz) return d.format(format);
  try {
    return d.tz(tz).format(format);
  } catch {
    // Invalid timezone — fall back to UTC
    return d.format(format);
  }
}

/**
 * Get short timezone abbreviation (e.g. "PT", "GMT", "EST").
 * Returns null when tz is null or invalid.
 */
export function getTimezoneAbbr(tz: string | null | undefined): string | null {
  if (!tz) return null;
  try {
    const abbr = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "short",
    })
      .formatToParts(new Date())
      .find((p) => p.type === "timeZoneName")?.value;
    return abbr || null;
  } catch {
    return null;
  }
}
